import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast, Toaster } from "sonner";
import {
  IdentificationCard, Clock, ClipboardText, Package, Megaphone,
  CalendarBlank, Brain, ChatTeardropText, CircleNotch, Timer,
  PlugsConnected,
} from "@phosphor-icons/react";

import LivePreview from "@/components/operacao/LivePreview";
// SetupWizard removido — wizard exibido apenas no Dashboard agora.
import IdentityTab from "@/components/operacao/IdentityTab";
import BusinessHoursTab from "@/components/operacao/BusinessHoursTab";
import ServicesTab from "@/components/operacao/ServicesTab";
import ProductsTab from "@/components/operacao/ProductsTab";
import PromotionsTab from "@/components/operacao/PromotionsTab";
import DailyBoardTab from "@/components/operacao/DailyBoardTab";
import KnowledgeTab from "@/components/operacao/KnowledgeTab";
import TemplatesTab from "@/components/operacao/TemplatesTab";
import IntervalsTab from "@/components/operacao/IntervalsTab";
import ConnectedSourcesTab from "@/components/operacao/ConnectedSourcesTab";
import Topbar from "@/components/Topbar";

/**
 * OIL Phase 2 — Central de Operação.
 *
 * Single screen, seven tabs. Each tab is the canonical source of truth
 * for one "category" the AI consults before answering (identity, hours,
 * services, promotions, daily board, qualitative knowledge, message
 * style references).
 *
 * Design intent: monochrome Apple-premium. Pill buttons, generous
 * negative space, no decorative gradients. Live preview right rail
 * mirrors what the AI will actually say given the current grounding.
 */

const TABS = [
  { id: "identity", label: "Identidade", icon: IdentificationCard,
    hint: "Como o Agentus se apresenta — tom, postura, frases proibidas." },
  { id: "hours", label: "Horários", icon: Clock,
    hint: "Quando o negócio atende — dias, intervalos, feriados." },
  { id: "services", label: "Serviços", icon: ClipboardText,
    hint: "Catálogo com preço e duração — única fonte pro Agentus." },
  { id: "products", label: "Produtos", icon: Package,
    hint: "Mercadorias físicas que você vende ou disponibiliza. O Agentus cita, mas não fecha venda." },
  { id: "promotions", label: "Ofertas", icon: Megaphone,
    hint: "Ofertas com prazo. O Agentus só anuncia o que está ativo." },
  { id: "board", label: "Disponibilidade", icon: CalendarBlank,
    hint: "Agenda do dia — capacidade, bloqueios e encaixes." },
  { id: "knowledge", label: "Base de Conhecimento", icon: Brain,
    hint: "Tudo que a IA precisa saber sobre o negócio além dos cadastros — contratos, políticas, FAQ, cartilhas, regras internas. O Cérebro consulta antes de cada resposta.",
    // v0.99.12 — Título "hero" da aba, separado do label do botão.
    // O botão continua curto ("Base de Conhecimento"); a página em si
    // se apresenta como "O cérebro do seu negócio" pra vibrar o conceito.
    bigTitle: "O cérebro do seu negócio",
    bigSubtitle: "Tudo que você quer que sua IA saiba mora aqui. Escreva, cole ou suba um arquivo — o AgentUS consulta antes de cada resposta no WhatsApp." },
  { id: "intervals", label: "Intervalos", icon: Timer,
    hint: "Prazos customizados de retorno e follow-up — sobrescreve o padrão do setor." },
  { id: "messages", label: "Vozes da IA", icon: ChatTeardropText,
    hint: "Como o AgentUS fala em cada momento — boas-vindas, handoff, confirmação, pós-atendimento, follow-up." },
  { id: "sources", label: "Fontes conectadas", icon: PlugsConnected,
    hint: "Escolha se o Cérebro é alimentado por você (autônomo) ou pelo seu sistema de gestão (integrado)." },
];

// Hash aliases — preserva URLs antigas (#templates) durante o cutover.
// Quando v0.78 sair (Phase 4 final) podemos remover esse mapa.
const HASH_ALIASES = { templates: "messages" };

function resolveTabFromHash(rawHash) {
  const hash = (rawHash || "").replace("#", "").trim();
  if (!hash) return null;
  const aliased = HASH_ALIASES[hash] || hash;
  return TABS.find((t) => t.id === aliased) ? aliased : null;
}

function StatusDot({ ok, partial }) {
  if (ok) return <span className="h-1.5 w-1.5 rounded-full bg-slate-900" />;
  if (partial) return <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />;
  return <span className="h-1.5 w-1.5 rounded-full bg-slate-200" />;
}

export default function Operacao() {
  // Derive initial tab from the URL hash so deep-links land on the right
  // panel on the FIRST render — using a lazy initializer to avoid the
  // mount-time race between a "read hash" effect and a "sync hash" effect
  // (the latter would overwrite the former before React committed the
  // state change). RCA: testing iteration_38.
  const [active, setActive] = useState(() => {
    if (typeof window === "undefined") return "identity";
    return resolveTabFromHash(window.location.hash) || "identity";
  });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  // v0.99.11 — Modo do Cérebro (autonomous | integrated).
  // Banner e comportamento read-only downstream leem daqui.
  const [modeInfo, setModeInfo] = useState({ mode: "autonomous", source_label: null });

  const fetchStatus = async () => {
    try {
      const res = await api.get("/operacao/status");
      setStatus(res.data);
    } catch (e) {
      console.error("operacao status failed", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchMode = async () => {
    try {
      const { data } = await api.get("/operacao/mode");
      setModeInfo({ mode: data.mode || "autonomous", source_label: data.source_label || null });
    } catch {
      /* falha silenciosa — comportamento default autonomous */
    }
  };

  useEffect(() => { fetchStatus(); fetchMode(); }, []);

  // Listen for in-app hash changes (e.g. SetupWizard CTAs hitting
  // /app/operacao#templates while this page is already mounted, or the
  // user manually editing the URL hash). The INITIAL mount is handled
  // by the lazy state initializer above — running it here too would
  // race with the sync-back effect below.
  useEffect(() => {
    const apply = () => {
      const tab = resolveTabFromHash(window.location.hash);
      if (tab) setActive(tab);
    };
    window.addEventListener("hashchange", apply);
    return () => window.removeEventListener("hashchange", apply);
  }, []);

  // Sync hash with active tab (so the operator can bookmark/share).
  useEffect(() => {
    if (window.location.hash.replace("#", "") !== active) {
      window.history.replaceState(null, "", `#${active}`);
    }
  }, [active]);

  const sections = status?.sections || {};
  const done = status?.done || 0;
  const total = status?.total || 6;

  const tabState = useMemo(() => ({
    identity: sections.identity?.configured,
    hours: sections.business_hours?.configured,
    services: sections.services?.configured,
    products: (sections.products?.count || 0) > 0,
    promotions: (sections.promotions?.count_active || 0) > 0,
    board: sections.daily_board?.configured,
    knowledge: sections.knowledge?.configured,
    messages: false, // refreshed when messages tab signals save
  }), [sections]);

  const ActiveComponent = {
    identity: IdentityTab,
    hours: BusinessHoursTab,
    services: ServicesTab,
    products: ProductsTab,
    promotions: PromotionsTab,
    board: DailyBoardTab,
    knowledge: KnowledgeTab,
    intervals: IntervalsTab,
    messages: TemplatesTab,
    sources: ConnectedSourcesTab,
  }[active];

  const activeMeta = TABS.find((t) => t.id === active);

  return (
    <div className="min-h-screen bg-white" data-testid="operacao-page">
      <Toaster theme="light" position="top-right" richColors />

      <Topbar
        title="Cérebro"
        subtitle="Tudo que o AgentUS sabe — identidade, vozes, serviços, horários e políticas. É o cérebro que sustenta cada conversa no WhatsApp."
        actions={
          <div
            data-testid="operacao-status-pill"
            className="flex items-center gap-3 px-4 py-2 rounded-full bg-slate-50 border border-slate-200"
          >
            {loading ? (
              <CircleNotch size={14} className="animate-spin text-slate-400" />
            ) : (
              <>
                <div className="text-[11px] text-slate-500 uppercase tracking-wider">
                  Setup
                </div>
                <div className="text-sm font-semibold text-slate-900 tabular-nums">
                  {done}/{total}
                </div>
              </>
            )}
          </div>
        }
      />

      <div className="px-8 pt-6 pb-12 flex gap-6 max-w-[1400px] mx-auto">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-4 font-semibold">
            Central do Cérebro
          </div>

          {/* v0.99.11 — Banner de modo integrado. Só aparece quando o dono
              declarou que um ERP alimenta o Cérebro. Serve pra evitar
              confusão do tipo "editei aqui e sumiu no próximo sync". */}
          {modeInfo.mode === "integrated" && active !== "sources" && (
            <div
              data-testid="operacao-integrated-banner"
              className="mb-4 flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3"
            >
              <div className="w-6 h-6 rounded-md bg-slate-900 text-white flex items-center justify-center shrink-0 mt-0.5">
                <PlugsConnected size={12} weight="fill" />
              </div>
              <div className="flex-1 min-w-0 text-[12.5px] text-slate-700 leading-relaxed">
                <strong className="text-slate-900">Cérebro integrado</strong>
                {modeInfo.source_label ? ` com ${modeInfo.source_label}` : ""}.
                {" "}Este cadastro é a visão do que a IA sabe hoje — edite no seu sistema para o próximo sync.
                {" "}
                <button
                  type="button"
                  onClick={() => setActive("sources")}
                  className="text-slate-900 underline hover:no-underline font-medium"
                  data-testid="operacao-banner-goto-sources"
                >
                  Gerenciar fontes
                </button>
              </div>
            </div>
          )}
          {/* Tabs */}
          <div
            data-testid="operacao-tabs"
            className="flex flex-wrap gap-2 mb-6 pb-4 border-b border-slate-100"
          >
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = active === tab.id;
              const isDone = tabState[tab.id];
              return (
                <button
                  key={tab.id}
                  type="button"
                  data-testid={`operacao-tab-${tab.id}`}
                  onClick={() => setActive(tab.id)}
                  className={`group inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] transition-all border ${
                    isActive
                      ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                      : "bg-white text-slate-600 border-slate-200 hover:border-slate-400 hover:text-slate-900"
                  }`}
                >
                  <Icon size={14} weight={isActive ? "fill" : "regular"} />
                  <span className="font-medium tracking-tight">{tab.label}</span>
                  <StatusDot ok={isDone && isActive ? false : isDone} />
                </button>
              );
            })}
          </div>

          {/* Active tab header */}
          <div className="mb-6">
            <div className="flex items-center gap-2 text-slate-900">
              <activeMeta.icon size={18} weight="regular" />
              <h2 className="text-xl font-semibold tracking-tight">
                {activeMeta.bigTitle || activeMeta.label}
              </h2>
            </div>
            <p className="text-sm text-slate-500 mt-1">
              {activeMeta.bigSubtitle || activeMeta.hint}
            </p>
          </div>

          {/* Active panel */}
          <div data-testid={`operacao-panel-${active}`}>
            <ActiveComponent
              onChange={() => { fetchStatus(); fetchMode(); }}
              onToast={(msg, kind = "success") => {
                // Use msg itself as toast id so sonner dedupes identical
                // calls fired in quick succession (avoids visual flicker
                // in dev's React StrictMode double-render).
                const opts = { id: `op-${active}-${msg.slice(0, 40)}` };
                if (kind === "success") toast.success(msg, opts);
                else if (kind === "error") toast.error(msg, opts);
                else toast(msg, opts);
              }}
            />
          </div>
        </div>

        {/* Right rail — Live preview */}
        <div className="hidden xl:block">
          <LivePreview />
        </div>
      </div>
    </div>
  );
}
